import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cloneTenant } from '../../services/apiSuperAdmin';

const CLONED_ITEMS = [
    { icon: 'person', label: 'Usuarios' },
    { icon: 'psychology', label: 'Estrategias de IA' },
    { icon: 'view_kanban', label: 'Etapas del Pipeline' },
    { icon: 'input_circle', label: 'Campos Personalizados' },
    { icon: 'chat_bubble', label: 'Plantillas de Mensajes' },
    { icon: 'webhook', label: 'Configuración Webhooks (desactivados)' },
    { icon: 'api', label: 'API Configs (desactivadas)' },
    { icon: 'rule', label: 'Reglas de Asignación (desactivadas)' },
    { icon: 'phone_iphone', label: 'Cuentas WhatsApp (sin conexión activa)' },
    { icon: 'photo_camera', label: 'Cuentas Instagram (sin token OAuth)' },
];

export default function CloneTenantModal({ isOpen, onClose, tenant }) {
    const qc = useQueryClient();
    const [step, setStep] = useState('form'); // 'form' | 'result'
    const [form, setForm] = useState({ new_name: '', new_slug: '', manager_password: '', confirm_password: '' });
    const [showPass, setShowPass] = useState(false);
    const [result, setResult] = useState(null);
    const [copiedAll, setCopiedAll] = useState(false);

    // Auto-generate slug from name
    useEffect(() => {
        if (form.new_name) {
            setForm(f => ({
                ...f,
                new_slug: f.new_name
                    .toLowerCase()
                    .normalize('NFD').replace(/\p{Diacritic}/gu, '')
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, ''),
            }));
        }
    }, [form.new_name]);

    const cloneMut = useMutation({
        mutationFn: (data) => cloneTenant(tenant.id, data),
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: ['super-admin-tenants'] });
            setResult(data);
            setStep('result');
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (form.manager_password !== form.confirm_password) return;
        cloneMut.mutate({
            new_name: form.new_name,
            new_slug: form.new_slug || undefined,
            manager_password: form.manager_password,
        });
    };

    const handleClose = () => {
        setStep('form');
        setForm({ new_name: '', new_slug: '', manager_password: '', confirm_password: '' });
        setResult(null);
        setCopiedAll(false);
        onClose();
    };

    const copyAllCredentials = () => {
        if (!result) return;
        const text = result.credentials
            .map(c => `${c.role.toUpperCase()}: ${c.name}\nEmail: ${c.email}\nPassword: ${c.password}`)
            .join('\n\n');
        navigator.clipboard.writeText(text);
        setCopiedAll(true);
        setTimeout(() => setCopiedAll(false), 2500);
    };

    const passMatch = form.manager_password && form.confirm_password && form.manager_password === form.confirm_password;
    const passMismatch = form.confirm_password && form.manager_password !== form.confirm_password;

    if (!isOpen || !tenant) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center gap-3 px-7 py-5 border-b border-gray-100 dark:border-slate-800">
                    <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-violet-600 dark:text-violet-400 text-[22px]">content_copy</span>
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                            {step === 'form' ? 'Clonar Tenant' : 'Clon Creado Exitosamente'}
                        </h2>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {step === 'form' ? `Fuente: ${tenant.name}` : `Nuevo tenant: ${result?.tenant?.name}`}
                        </p>
                    </div>
                    <button onClick={handleClose} className="ml-auto p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-400">
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>

                <div className="overflow-y-auto flex-1">
                    {step === 'form' ? (
                        <form onSubmit={handleSubmit} className="p-7 space-y-6">
                            {/* What gets cloned preview */}
                            <div>
                                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">¿Qué se clona?</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {CLONED_ITEMS.map(item => (
                                        <div key={item.label} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                            <span className="material-symbols-outlined text-emerald-500 text-[16px]">check_circle</span>
                                            <span className="material-symbols-outlined text-slate-400 text-[15px]">{item.icon}</span>
                                            {item.label}
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                    <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[16px]">info</span>
                                        <strong>No se clonan:</strong> Leads, contactos, conversaciones, mensajes, ni tokens OAuth activos.
                                    </p>
                                </div>
                            </div>

                            {/* New tenant info */}
                            <div className="space-y-4">
                                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Datos del nuevo tenant</p>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Nombre del clon *</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder={`Clon de ${tenant.name}`}
                                        value={form.new_name}
                                        onChange={e => setForm(f => ({ ...f, new_name: e.target.value }))}
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Slug (auto-generado)</label>
                                    <input
                                        type="text"
                                        value={form.new_slug}
                                        onChange={e => setForm(f => ({ ...f, new_slug: e.target.value }))}
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                        Contraseña para el Manager *
                                    </label>
                                    <p className="text-xs text-slate-400 mb-2">Los demás usuarios recibirán contraseñas aleatorias. Guárdalas del resultado.</p>
                                    <div className="relative">
                                        <input
                                            required
                                            type={showPass ? 'text' : 'password'}
                                            placeholder="Mínimo 8 chars, mayúsculas y números"
                                            value={form.manager_password}
                                            onChange={e => setForm(f => ({ ...f, manager_password: e.target.value }))}
                                            className="w-full px-4 py-2.5 pr-10 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                                        />
                                        <button type="button" onClick={() => setShowPass(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                                            <span className="material-symbols-outlined text-[18px]">{showPass ? 'visibility_off' : 'visibility'}</span>
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Confirmar contraseña *</label>
                                    <input
                                        required
                                        type={showPass ? 'text' : 'password'}
                                        placeholder="Repetir contraseña"
                                        value={form.confirm_password}
                                        onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))}
                                        className={`w-full px-4 py-2.5 rounded-xl border bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 transition-colors ${
                                            passMismatch
                                                ? 'border-red-400 focus:ring-red-500/30'
                                                : passMatch
                                                    ? 'border-emerald-400 focus:ring-emerald-500/30'
                                                    : 'border-gray-200 dark:border-slate-700 focus:ring-violet-500/30 focus:border-violet-400'
                                        }`}
                                    />
                                    {passMismatch && <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>}
                                </div>
                            </div>

                            {cloneMut.isError && (
                                <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                                    <p className="text-xs text-red-600 dark:text-red-400">
                                        {cloneMut.error?.response?.data?.message || 'Error al clonar el tenant.'}
                                    </p>
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={handleClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-gray-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={cloneMut.isPending || passMismatch || !form.new_name || !form.manager_password}
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white shadow-lg shadow-violet-500/20 transition-all flex items-center justify-center gap-2"
                                >
                                    {cloneMut.isPending ? (
                                        <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Clonando...</>
                                    ) : (
                                        <><span className="material-symbols-outlined text-[18px]">content_copy</span> Crear Clon</>
                                    )}
                                </button>
                            </div>
                        </form>
                    ) : (
                        /* Result screen */
                        <div className="p-7 space-y-5">
                            <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                                <span className="material-symbols-outlined text-emerald-500 text-[28px]">check_circle</span>
                                <div>
                                    <p className="font-bold text-emerald-800 dark:text-emerald-300">¡Tenant clonado exitosamente!</p>
                                    <p className="text-xs text-emerald-600 dark:text-emerald-400">ID: #{result?.tenant?.id} · Slug: /{result?.tenant?.slug}</p>
                                </div>
                            </div>

                            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                <p className="text-xs text-amber-700 dark:text-amber-400 flex gap-2">
                                    <span className="material-symbols-outlined text-[16px] shrink-0 mt-0.5">warning</span>
                                    <span><strong>¡Importante!</strong> Las contraseñas temporales se muestran <strong>una sola vez</strong>. Cópialas ahora y compártelas de forma segura con los usuarios.</span>
                                </p>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Credenciales de acceso</p>
                                    <button
                                        onClick={copyAllCredentials}
                                        className="flex items-center gap-1.5 text-xs font-medium text-violet-600 dark:text-violet-400 hover:text-violet-700 transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[15px]">{copiedAll ? 'check' : 'content_copy'}</span>
                                        {copiedAll ? '¡Copiado!' : 'Copiar todo'}
                                    </button>
                                </div>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {result?.credentials?.map((cred, i) => (
                                        <CredentialCard key={i} credential={cred} />
                                    ))}
                                </div>
                            </div>

                            <button
                                onClick={handleClose}
                                className="w-full py-2.5 rounded-xl text-sm font-bold bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-500/20 transition-all"
                            >
                                Entendido, cerrar
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function CredentialCard({ credential }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(credential.password);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    const roleColors = {
        manager: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
        admin:   'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
        agent:   'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
    };
    return (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700">
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{credential.name}</p>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${roleColors[credential.role] || roleColors.agent}`}>
                        {credential.role}
                    </span>
                </div>
                <p className="text-xs text-slate-400 truncate">{credential.email}</p>
                <p className="text-xs font-mono text-slate-600 dark:text-slate-300 mt-1 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-lg inline-block">{credential.password}</p>
            </div>
            <button onClick={copy} className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 transition-colors shrink-0">
                <span className="material-symbols-outlined text-[16px]">{copied ? 'check' : 'content_copy'}</span>
            </button>
        </div>
    );
}
